import { useState, useEffect, useCallback, Fragment } from 'react';
import { Link } from 'react-router-dom';
import api from 'services/api';

function saApi() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('gss_sa_token')}` } };
}

const CAT_LABELS = {
  offre: 'Offres abonnement',
  onboarding: 'Onboarding',
  support: 'Support premium',
  formation: 'Formations complémentaires',
  module: 'Modules optionnels',
};

const CAT_ORDER = ['offre', 'onboarding', 'support', 'formation', 'module'];
const CAT_COLORS = { offre: 'var(--gss-red)', onboarding: 'var(--gss-info)', support: 'var(--gss-success)', formation: '#A855F7', module: 'var(--gss-warning)' };

/* Demain, au format que l input date attend. Un prix prend effet DEMAIN au plus
 * tot : `date_fin` est le premier jour NON couvert, donc poser un prix
 * aujourd hui changerait une journee deja facturee. Le serveur le refuse ; on
 * evite a l utilisateur de decouvrir la regle par un refus. */
function demain() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

const fr = (d) => new Date(d).toLocaleDateString('fr-FR');

/* ⚠️ UNE DATE D EFFET NE SE LIT PAS TOUTE SEULE — file 93.
 * Les 20 prix de production sont dates du 01/01/2023 : une ANCRE TECHNIQUE
 * posee par la migration 064 pour qu aucune piece deja emise ne se retrouve
 * anterieure a sa propre grille. **GSS-360 n existait pas** : le depot commence
 * le 20/05/2026 et la premiere facture SaaS est du 11/06/2026.
 * ➡️ La ligne DIT ce que sa date vaut, et l ecran le repete :
 *      `reelle` -> « depuis le JJ/MM/AAAA », la date a laquelle le prix a ete
 *                  decide par quelqu un, a cet ecran ;
 *      `borne`  -> « avant le JJ/MM/AAAA », et on avoue que l origine n a
 *                  jamais ete enregistree.
 * ⛔ LA VALEUR TECHNIQUE NE S AFFICHE JAMAIS pour une borne. *Un chiffre faux
 *    qu on affiche cesse d etre une ligne de base et devient une affirmation*
 *    (Dom, 21/09/2026).
 * ⓘ Repli si la nature manque : on affiche « - ». Ne RIEN dire vaut mieux que
 *   dire une date dont on ignore ce qu elle vaut. */
function enVigueur(item) {
  if (item.nature_date_effet === 'borne' && item.borne_date_effet) {
    return { texte: `avant le ${fr(item.borne_date_effet)}`,
             aveu: 'date d’origine non historisée' };
  }
  if (item.nature_date_effet === 'reelle' && item.date_effet) {
    return { texte: `depuis le ${fr(item.date_effet)}`, aveu: null };
  }
  return { texte: '-', aveu: null };
}

export default function SuperAdminTarifs() {
  const [tarifs, setTarifs] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState({});
  const [saving, setSaving] = useState(false);
  const [refus, setRefus] = useState(null);

  /* ⚠️ UN ÉCRAN DE PRIX SANS PAYS EST ILLISIBLE DÈS LE SECOND PAYS. Jusqu’au
   *    22/09/2026, cet écran affichait toute la grille sans dire de quel pays
   *    venait chaque ligne — invisible tant qu’un seul pays avait des prix.
   *    Le jour où un second en a, deux grilles se mélangent et « Nouveau prix »
   *    agit sur une ligne dont on ne voit pas le pays. */
  const [pays, setPays] = useState('TG');
  const [listePays, setListePays] = useState([]);
  const [etatPays, setEtatPays] = useState(null);
  const [posant, setPosant] = useState(null);     // code dont on pose le premier prix
  const [posVal, setPosVal] = useState({});
  const [ouvrant, setOuvrant] = useState(false);  // ouverture du pays par recopie
  const [ouvVal, setOuvVal] = useState({ date_effet: '', motif: '' });

  /* ⚠️ `useCallback` PLUTOT QU UN `eslint-disable`, ET C EST LE GARDE QUI L A
   *    IMPOSE : la dette `exhaustive-deps` est PLAFONNEE a 4 trous et ne peut
   *    que decroitre. Desactiver la regle en aurait ouvert un cinquieme — *une
   *    dette a decroissance obligatoire ne se paie pas en s y ajoutant.*
   *    ➡️ La fonction est stable tant que le pays ne change pas ; l effet la
   *       declare franchement, et il n y a plus rien a taire. */
  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      api.get(`/super-admin/tarifs?pays=${encodeURIComponent(pays)}`, saApi())
        .then(r => setTarifs(r.data)).catch(() => setTarifs({})),
      api.get(`/super-admin/tarifs/pays/${encodeURIComponent(pays)}/manques`, saApi())
        .then(r => setEtatPays(r.data)).catch(() => setEtatPays(null)),
    ]).finally(() => setLoading(false));
  }, [pays]);

  useEffect(() => {
    api.get('/super-admin/tarifs/pays', saApi())
      .then(r => setListePays(r.data || [])).catch(() => setListePays([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setRefus(null);
    setEditing(item.id);
    setEditVal({ montant: item.montant ?? '', date_effet: demain(), motif: '' });
  };

  /* ⚠️ CE N EST PLUS UNE MODIFICATION, C EST UN REMPLACEMENT DATE.
   *    La ligne en vigueur n est pas reecrite : elle est CLOTUREE a la date
   *    choisie, et une nouvelle prend le relais. Une facture deja emise garde
   *    donc son prix — c est tout l objet de la manoeuvre. */
  const saveEdit = async (id) => {
    setSaving(true);
    setRefus(null);
    try {
      await api.put(`/super-admin/tarifs/${id}`, {
        montant: editVal.montant === '' || editVal.montant === null ? null : Number(editVal.montant),
        date_effet: editVal.date_effet,
        motif: editVal.motif,
      }, saApi());
      setEditing(null);
      load();
    } catch (err) {
      /* Le refus du serveur est une phrase destinee a etre lue : on l affiche
       * telle quelle, a cote de la ligne, jamais dans une alerte qui disparait. */
      setRefus(err.response?.data?.error
        || 'Le tarif n’a pas pu être enregistré. Aucune valeur n’a été changée.');
    } finally { setSaving(false); }
  };

  const demarrerPose = (c) => {
    setRefus(null);
    setPosant(c.code);
    setPosVal({ montant: '', date_effet: demain(), motif: '',
                montant_plancher: '', effectif_min: '', effectif_max: '' });
  };

  /* ⚠️ CE QUI N’EST PAS DANS CE FORMULAIRE EST AUSSI IMPORTANT QUE CE QUI Y EST.
   *    L’unité — « par agent » ou « forfait » — et la clé du module viennent du
   *    CATALOGUE ; la devise vient du référentiel du pays. Aucune des trois ne
   *    se saisit : une unité tapée à la main déciderait si le montant est
   *    multiplié par l’effectif. */
  const poserPremier = async (c) => {
    setSaving(true);
    setRefus(null);
    try {
      await api.post('/super-admin/tarifs', {
        pays, code: c.code,
        montant: posVal.montant === '' ? null : Number(posVal.montant),
        date_effet: posVal.date_effet,
        motif: posVal.motif,
        montant_plancher: posVal.montant_plancher === '' ? null : Number(posVal.montant_plancher),
        effectif_min: posVal.effectif_min === '' ? null : Number(posVal.effectif_min),
        effectif_max: posVal.effectif_max === '' ? null : Number(posVal.effectif_max),
      }, saApi());
      setPosant(null);
      load();
    } catch (err) {
      setRefus(err.response?.data?.error
        || 'Le prix n’a pas pu être posé. Aucune valeur n’a été enregistrée.');
    } finally { setSaving(false); }
  };

  /* ⚖️ LE CHEMIN PAR DEFAUT — les offres ont UN SEUL PRIX pour tous les pays
   *    (Dom, 22/09/2026) : ouvrir un pays n’est pas une décision de prix, c’est
   *    une recopie depuis le pays de référence. Poser un prix à la main reste
   *    possible juste en dessous ; ce n’est plus le geste normal. */
  const ouvrirPays = async () => {
    setSaving(true);
    setRefus(null);
    try {
      await api.post(`/super-admin/tarifs/pays/${encodeURIComponent(pays)}/ouvrir`, {
        date_effet: ouvVal.date_effet, motif: ouvVal.motif,
      }, saApi());
      setOuvrant(false);
      load();
    } catch (err) {
      setRefus(err.response?.data?.error
        || 'Le pays n’a pas pu être ouvert. Aucun prix n’a été posé.');
    } finally { setSaving(false); }
  };

  const paysCourant = listePays.find(p => p.pays_code === pays);

  return (
    <div style={S.page}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/super-admin" style={{ color: 'var(--gss-text-light)', fontSize: 12, textDecoration: 'none' }}>Dashboard</Link>
        <span style={{ color: 'var(--gss-text-light)', margin: '0 8px' }}>/</span>
        <span style={{ color: 'var(--gss-text)', fontSize: 14, fontWeight: 700 }}>Grille tarifaire SaaS</span>
      </div>

      {/* ── LE PAYS SE CHOISIT AVANT TOUT : un prix appartient à un pays ── */}
      <div style={S.barrePays} data-testid="selecteur-pays">
        <label htmlFor="pays-grille" style={{ ...S.hint, marginRight: 8 }}>Pays</label>
        <select id="pays-grille" value={pays} onChange={e => setPays(e.target.value)}
                className="gss-input" style={{ maxWidth: 320 }} data-testid="choix-pays">
          {listePays.map(p => (
            <option key={p.pays_code} value={p.pays_code}>
              {p.libelle} ({p.pays_code}) — {p.devise}
              {p.facturable ? ' · facturable' : ` · ${p.lignes === 0 ? 'aucun prix' : 'incomplet'}`}
            </option>
          ))}
        </select>
        {paysCourant && (
          <span style={{ ...S.hint, marginLeft: 12 }}>
            Devise : <strong>{paysCourant.devise}</strong> — lue au référentiel du pays, jamais convertie.
          </span>
        )}
      </div>

      {loading && <p style={{ color: 'var(--gss-text-light)' }}>Chargement...</p>}

      {/* ── CE QUI MANQUE POUR QUE CE PAYS SOIT FACTURABLE ────────────────────
          ⚠️ Un tableau vide laisse croire qu’il n’y a rien à faire. Ici l’écran
             NOMME chaque prestation qui attend son prix. */}
      {!loading && etatPays && etatPays.codes_manquants && etatPays.codes_manquants.length > 0 && (
        <div style={S.aPoser} data-testid="etat-pays-incomplet">
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {Object.keys(tarifs).length === 0
              ? `Aucun prix n’est posé pour ${etatPays.libelle}.`
              : `La grille de ${etatPays.libelle} est incomplète.`}
          </div>
          <div style={{ marginBottom: 12 }}>
            Tant que ces prestations n’ont pas de prix, aucun client de ce pays ne peut être créé ni facturé.
          </div>

          {/* ── LE GESTE PAR DÉFAUT : OUVRIR AUX PRIX DE RÉFÉRENCE ──────────── */}
          {etatPays.recopie_possible ? (
            <div style={S.ouverture} data-testid="bloc-ouverture">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Ouvrir ce pays aux prix de la grille de référence
              </div>
              <div style={{ marginBottom: 10 }}>
                Les offres ont un seul prix, valable pour tous les pays. Ce geste recopie les prix
                en vigueur dans le pays de référence ({etatPays.pays_reference}) — les deux pays
                partagent la même parité fixe, aucun montant n’est converti. Les prestations
                négociées ne sont pas recopiées.
              </div>
              {ouvrant ? (
                <div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <label style={S.hint} htmlFor="ouv-date">Date d’effet</label>
                      <input id="ouv-date" type="date" min={demain()}
                             value={ouvVal.date_effet || demain()}
                             onChange={e => setOuvVal({ ...ouvVal, date_effet: e.target.value })}
                             style={{ ...S.inlineInput, width: 150 }} data-testid="ouv-date" />
                    </div>
                    <button onClick={ouvrirPays} disabled={saving} style={S.btnSm}
                            data-testid="ouv-ok">Ouvrir le pays</button>
                    <button onClick={() => { setOuvrant(false); setRefus(null); }}
                            style={{ ...S.btnSm, background: 'transparent',
                                     border: '1px solid var(--gss-border-strong)',
                                     color: 'var(--gss-text-light)' }}>Annuler</button>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={S.hint} htmlFor="ouv-motif">
                      Motif — 12 caractères au moins, il reste dans l’historique
                    </label>
                    <input id="ouv-motif" value={ouvVal.motif}
                           onChange={e => setOuvVal({ ...ouvVal, motif: e.target.value })}
                           placeholder="Pourquoi ce pays est ouvert"
                           style={{ ...S.inlineInput, width: '100%' }} data-testid="ouv-motif" />
                  </div>
                </div>
              ) : (
                <button onClick={() => { setRefus(null); setOuvrant(true);
                                         setOuvVal({ date_effet: demain(), motif: '' }); }}
                        style={S.btnEdit} data-testid="ouvrir-pays">
                  Ouvrir aux prix de référence
                </button>
              )}
            </div>
          ) : (
            <div style={{ ...S.hint, marginBottom: 12 }}>
              Ce pays ne peut pas être ouvert par recopie : sa devise ({paysCourant?.devise}) ne partage
              aucune parité fixe avec celle du pays de référence. Aucun montant ne se convertit —
              ses prix se posent ligne par ligne, ci-dessous.
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {etatPays.codes_manquants.map(c => (
                <Fragment key={c.code}>
                  <tr>
                    <td style={{ ...S.td, color: 'var(--gss-text)', fontWeight: 500 }}>
                      {c.label || c.code}
                      <div style={S.hint}>{c.categorie} · {c.unite}</div>
                    </td>
                    <td style={{ ...S.td, width: 180, textAlign: 'right' }}>
                      {posant === c.code
                        ? <button onClick={() => { setPosant(null); setRefus(null); }}
                                  style={{ ...S.btnSm, background: 'transparent',
                                           border: '1px solid var(--gss-border-strong)',
                                           color: 'var(--gss-text-light)' }}>Annuler</button>
                        : <button onClick={() => demarrerPose(c)} style={S.btnEdit}
                                  data-testid={`poser-${c.code}`}>Poser le premier prix</button>}
                    </td>
                  </tr>
                  {posant === c.code && (
                    <tr>
                      <td colSpan={2} style={{ ...S.td, background: 'var(--gss-bg)' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div>
                            <label style={S.hint} htmlFor={`m-${c.code}`}>Montant ({paysCourant?.devise})</label>
                            <input id={`m-${c.code}`} type="number" value={posVal.montant}
                                   onChange={e => setPosVal({ ...posVal, montant: e.target.value })}
                                   style={{ ...S.inlineInput, width: 130 }} data-testid="pose-montant" />
                          </div>
                          {c.categorie === 'offre' && (
                            <>
                              <div>
                                <label style={S.hint} htmlFor={`p-${c.code}`}>Plancher mensuel</label>
                                <input id={`p-${c.code}`} type="number" value={posVal.montant_plancher}
                                       onChange={e => setPosVal({ ...posVal, montant_plancher: e.target.value })}
                                       style={{ ...S.inlineInput, width: 130 }} data-testid="pose-plancher" />
                              </div>
                              <div>
                                <label style={S.hint} htmlFor={`emin-${c.code}`}>Effectif minimum</label>
                                <input id={`emin-${c.code}`} type="number" value={posVal.effectif_min}
                                       onChange={e => setPosVal({ ...posVal, effectif_min: e.target.value })}
                                       style={{ ...S.inlineInput, width: 110 }} data-testid="pose-emin" />
                              </div>
                              <div>
                                <label style={S.hint} htmlFor={`emax-${c.code}`}>Effectif maximum</label>
                                <input id={`emax-${c.code}`} type="number" value={posVal.effectif_max}
                                       onChange={e => setPosVal({ ...posVal, effectif_max: e.target.value })}
                                       style={{ ...S.inlineInput, width: 110 }} data-testid="pose-emax" />
                              </div>
                            </>
                          )}
                          <div>
                            <label style={S.hint} htmlFor={`d-${c.code}`}>Date d’effet</label>
                            <input id={`d-${c.code}`} type="date" min={demain()} value={posVal.date_effet}
                                   onChange={e => setPosVal({ ...posVal, date_effet: e.target.value })}
                                   style={{ ...S.inlineInput, width: 150 }} data-testid="pose-date" />
                          </div>
                          <button onClick={() => poserPremier(c)} disabled={saving}
                                  style={S.btnSm} data-testid="pose-ok">Poser</button>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <label style={S.hint} htmlFor={`mo-${c.code}`}>
                            Motif — 12 caractères au moins, il reste dans l’historique
                          </label>
                          <input id={`mo-${c.code}`} value={posVal.motif}
                                 onChange={e => setPosVal({ ...posVal, motif: e.target.value })}
                                 placeholder="Pourquoi ce prix est posé"
                                 style={{ ...S.inlineInput, width: '100%' }} data-testid="pose-motif" />
                        </div>
                        <div style={{ ...S.hint, marginTop: 6 }}>
                          L’unité et la devise ne se saisissent pas : elles viennent du catalogue des
                          prestations et du référentiel du pays.
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ⚠️ LA REGLE SE DIT AVANT LE GESTE, PAS APRES LE REFUS. */}
      <div style={S.avis} data-testid="avis-tarif-date">
        Un tarif en vigueur n’est jamais réécrit : il est remplacé à une date. La valeur
        que vous posez prend effet le jour choisi — demain au plus tôt — et l’ancienne
        est clôturée ce jour-là. Les factures déjà émises gardent leur prix.
      </div>

      {refus && (
        <div style={S.refus} data-testid="refus-tarif">{refus}</div>
      )}

      {CAT_ORDER.map(cat => {
        const items = tarifs[cat];
        if (!items || items.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: CAT_COLORS[cat] || 'var(--gss-text)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 2 }}>
              {CAT_LABELS[cat] || cat}
            </div>
            <div style={S.card}>
              <div className="gss-table-wrap">
              <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width: 70 }}>Pays</th>
                    <th style={S.th}>Désignation</th>
                    <th style={{ ...S.th, width: 140 }}>Montant</th>
                    <th style={{ ...S.th, width: 160 }}>Unité</th>
                    {/* ⚠️ « En vigueur DEPUIS » etait faux pour une ancre technique :
                        l en-tete affirmait une origine que la ligne ne connait pas.
                        C est la cellule qui dit « depuis » ou « avant » (file 93). */}
                    <th style={{ ...S.th, width: 150 }}>En vigueur</th>
                    {cat === 'support' && <th style={{ ...S.th, width: 220 }}>Description</th>}
                    <th style={{ ...S.th, width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <Fragment key={item.id}>
                    <tr>
                      {editing === item.id ? (
                        <>
                          <td style={{ ...S.td, color: 'var(--gss-text-light)', fontSize: 12 }}>{item.pays_code}</td>
                          <td style={{ ...S.td, color: 'var(--gss-text)', fontWeight: 500 }}>
                            {item.label}
                            {/* ⛔ Le libelle et l unite ne sont plus modifiables ici :
                                les reecrire toucherait une ligne deja appliquee, que
                                la base protege. Seule la VALEUR se redate. */}
                            <div style={S.hint}>Nouvelle valeur datée</div>
                          </td>
                          <td style={S.td}>
                            <input type="number" value={editVal.montant ?? ''} onChange={e => setEditVal({ ...editVal, montant: e.target.value })} style={{ ...S.inlineInput, width: 110 }} placeholder="Montant" data-testid="saisie-montant" />
                          </td>
                          <td style={{ ...S.td, color: 'var(--gss-text-light)', fontSize: 12 }}>{item.unite}</td>
                          <td style={S.td}>
                            <input type="date" min={demain()} value={editVal.date_effet || ''} onChange={e => setEditVal({ ...editVal, date_effet: e.target.value })} style={{ ...S.inlineInput, width: 140 }} data-testid="saisie-date-effet" />
                          </td>
                          {cat === 'support' && <td style={S.td} />}
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => saveEdit(item.id)} disabled={saving} style={S.btnSm}>OK</button>
                              <button onClick={() => { setEditing(null); setRefus(null); }} style={{ ...S.btnSm, background: 'transparent', border: '1px solid var(--gss-border-strong)', color: 'var(--gss-text-light)' }}>X</button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ ...S.td, color: 'var(--gss-text-light)', fontSize: 12 }}>{item.pays_code}</td>
                          <td style={{ ...S.td, color: 'var(--gss-text)', fontWeight: 500 }}>{item.label}</td>
                          <td style={{ ...S.td, color: item.montant == null ? 'var(--gss-text-light)' : 'var(--gss-red)', fontWeight: 700 }}>
                            {item.montant == null ? 'Sur devis' : Number(item.montant).toLocaleString() + ' F'}
                          </td>
                          <td style={{ ...S.td, color: 'var(--gss-text-light)', fontSize: 12 }}>{item.unite}</td>
                          <td style={{ ...S.td, color: 'var(--gss-text-light)', fontSize: 12 }}
                              data-testid="en-vigueur">
                            {enVigueur(item).texte}
                            {enVigueur(item).aveu && (
                              <div style={{ ...S.hint, marginTop: 2 }}>{enVigueur(item).aveu}</div>
                            )}
                          </td>
                          {cat === 'support' && <td style={{ ...S.td, color: 'var(--gss-text-light)', fontSize: 12 }}>{item.description || '-'}</td>}
                          <td style={S.td}>
                            {item.sur_devis
                              ? <span style={{ ...S.hint, display: 'block' }}>Tarif négocié sur la fiche du client</span>
                              : <button onClick={() => startEdit(item)} style={S.btnEdit}>Nouveau prix</button>}
                          </td>
                        </>
                      )}
                    </tr>
                    {editing === item.id && (
                      /* ⚠️ LE MOTIF EST EXIGE PAR LA BASE, ET IL A UNE RAISON :
                         sans lui, une correction d erreur et un changement de
                         tarif sont indistinguables dans l historique. On lui
                         donne toute la largeur plutot qu une cellule etroite —
                         un champ trop petit se remplit par « maj ». */
                      <tr>
                        <td style={{ ...S.td, background: 'var(--gss-bg)' }} colSpan={cat === 'support' ? 7 : 6}>
                          <label style={S.hint} htmlFor={`motif-${item.id}`}>
                            Motif du changement — 12 caractères au moins, il reste dans l’historique
                          </label>
                          <input
                            id={`motif-${item.id}`}
                            value={editVal.motif || ''}
                            onChange={e => setEditVal({ ...editVal, motif: e.target.value })}
                            placeholder="Pourquoi ce prix change"
                            style={{ ...S.inlineInput, marginTop: 4 }}
                            data-testid="saisie-motif"
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const S = {
  barrePays: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
               padding: '12px 16px', marginBottom: 16, borderRadius: 8,
               background: 'var(--gss-surface)', border: '1px solid var(--gss-border)' },
  ouverture: { padding: '12px 14px', marginBottom: 16, borderRadius: 6,
               background: 'var(--gss-bg)', border: '1px solid var(--gss-border-strong)' },
  aPoser: { padding: '16px 18px', marginBottom: 24, borderRadius: 8,
            background: 'var(--gss-surface)', border: '1px solid var(--gss-warning)',
            color: 'var(--gss-text-light)', fontSize: 13, lineHeight: 1.6 },
  page: { width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: 'var(--gss-bg)', padding: 'clamp(12px, 3vw, 28px)' },
  card: { background: 'var(--gss-white)', borderRadius: 12, padding: 0, border: '1px solid var(--gss-border)', overflowX: 'auto', minWidth: 0 },
  th: { textAlign: 'left', padding: '10px 14px', fontSize: 10, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid var(--gss-border)', background: 'var(--gss-bg)' },
  td: { padding: '10px 14px', borderBottom: '1px solid var(--gss-border)', fontSize: 13, color: 'var(--gss-text-secondary)' },
  btnEdit: { padding: '3px 10px', borderRadius: 6, border: '1px solid var(--gss-border-strong)', background: 'transparent', color: 'var(--gss-red)', fontSize: 11, cursor: 'pointer' },
  btnSm: { padding: '3px 10px', borderRadius: 6, border: 'none', background: 'var(--gss-red)', color: '#FFF', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  inlineInput: { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gss-border-strong)', background: 'var(--gss-bg)', color: 'var(--gss-text)', fontSize: 12, outline: 'none', width: '100%', boxSizing: 'border-box' },
  avis: { marginBottom: 16, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--gss-border)', background: 'var(--gss-white)', color: 'var(--gss-text-secondary)', fontSize: 12, lineHeight: 1.5 },
  refus: { marginBottom: 16, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--gss-red)', background: 'var(--gss-white)', color: 'var(--gss-red)', fontSize: 13, fontWeight: 600, lineHeight: 1.5 },
  hint: { fontSize: 11, color: 'var(--gss-text-light)' },
};
